import { Layout, Menu, Avatar, Dropdown, Typography, Space, Button } from "antd";
import {
  UserOutlined,
  ReadOutlined,
  ShoppingOutlined,
  LogoutOutlined,
  HomeOutlined,
  CalendarOutlined,
  ScheduleOutlined,
  RocketOutlined,
  DashboardOutlined,
  QuestionCircleOutlined,
  SafetyCertificateOutlined,
  LockOutlined,
  FormOutlined,
} from "@ant-design/icons";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";
import { InstallPrompt } from "@/components/InstallPrompt";
import { NotificationBell } from "@/components/NotificationBell";
import { isAdminRole } from "@/admin/types";

const { Header, Content, Footer } = Layout;
const { Text } = Typography;

export function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // TT 83 C1-c — chỉ học viên + khi flag bật.
  const showIdentity = user?.role === "student" && !!user?.feature_flags?.identity_ekyc;

  const userMenuItems = [
    {
      key: "profile",
      label: "Hồ sơ",
      icon: <UserOutlined />,
      onClick: () => navigate("/profile"),
    },
    {
      // TT 83 C7-b — quyền chủ thể dữ liệu (NĐ 13)
      key: "my-data",
      label: "Dữ liệu của tôi",
      icon: <LockOutlined />,
      onClick: () => navigate("/my-data"),
    },
    ...(user?.role === "student" && user?.feature_flags?.surveys
      ? [
          {
            // TT 83 C9 — khảo sát hài lòng
            key: "surveys",
            label: "Khảo sát",
            icon: <FormOutlined />,
            onClick: () => navigate("/surveys"),
          },
        ]
      : []),
    ...(user?.feature_flags?.live_defense
      ? [
          {
            // TT 83 C10 — phiên bảo vệ (người bảo vệ + hội đồng)
            key: "defenses",
            label: "Bảo vệ khoá luận",
            icon: <SafetyCertificateOutlined />,
            onClick: () => navigate("/defenses"),
          },
        ]
      : []),
    ...(showIdentity
      ? [
          {
            key: "identity",
            label: "Xác thực danh tính",
            icon: <SafetyCertificateOutlined />,
            onClick: () => navigate("/identity-verify"),
          },
        ]
      : []),
    { type: "divider" as const },
    {
      key: "logout",
      label: "Đăng xuất",
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  const navItems = [
    { key: "/", label: <Link to="/">Trang chủ</Link>, icon: <HomeOutlined /> },
    {
      key: "/study-plan",
      label: <Link to="/study-plan">Kế hoạch học tập</Link>,
      icon: <CalendarOutlined />,
    },
    {
      key: "/my-courses",
      label: <Link to="/my-courses">Môn học của tôi</Link>,
      icon: <ReadOutlined />,
    },
    {
      // 2026-10-01 — lịch kiểm tra / thi / buổi live / bảo vệ khoá luận
      key: "/calendar",
      label: <Link to="/calendar">Lịch</Link>,
      icon: <ScheduleOutlined />,
    },
    {
      key: "/my-orders",
      label: <Link to="/my-orders">Đơn hàng</Link>,
      icon: <ShoppingOutlined />,
    },
    {
      // Sprint 24 B4 — Career Connect entry point cho học viên đã login
      key: "/careers",
      label: <Link to="/careers">Việc làm</Link>,
      icon: <RocketOutlined />,
    },
  ];

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Header style={{ display: "flex", alignItems: "center", padding: "0 24px" }}>
        <div style={{ color: "#fff", fontSize: 18, fontWeight: 600, marginRight: 32 }}>
          E-learning 2026
        </div>
        <Menu
          theme="dark"
          mode="horizontal"
          selectedKeys={[
            // /my-courses/:slug (chi tiết môn) vẫn sáng mục "Môn học của tôi".
            location.pathname.startsWith("/my-courses/") ? "/my-courses" : location.pathname,
          ]}
          items={navItems}
          style={{ flex: 1, minWidth: 0 }}
        />
        <div style={{ marginRight: 12 }}>
          <NotificationBell dark />
        </div>
        {user && isAdminRole(user.role) && (
          <Button
            ghost
            icon={<DashboardOutlined />}
            onClick={() => navigate("/admin")}
            style={{ marginRight: 16 }}
          >
            Trang Quản trị
          </Button>
        )}
        {user && (
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space style={{ cursor: "pointer", color: "#fff" }}>
              <Avatar icon={<UserOutlined />} />
              <Text style={{ color: "#fff" }}>{user.email}</Text>
            </Space>
          </Dropdown>
        )}
      </Header>
      <Content style={{ padding: "24px", background: "#f4f5f7" }}>
        {/* Sprint 9 B5 — PWA install prompt sau 3 page views + browser support */}
        <InstallPrompt />
        <Outlet />
      </Content>
      <Footer style={{ textAlign: "center" }}>
        <Space split={<span>·</span>}>
          <span>F-Future E-learning Platform © {new Date().getFullYear()}</span>
          <Link to="/huong-dan-su-dung">
            <QuestionCircleOutlined /> Hướng dẫn sử dụng
          </Link>
        </Space>
      </Footer>
    </Layout>
  );
}
